import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { InitiativeSection } from '../initiatives/InitiativeSection';
import { cognocracyComponents, cognocracyOpenQuestions } from '../../data/initiatives';
import { fadeUp } from '../../utils/motion';
const offsetClass = 'lg:ml-[calc(41.666%+1.667rem)]';
export function CognocracyGovernance() {
  return <>
      <section aria-labelledby="capability-governance" className="bg-navy-deep text-white py-20 lg:py-28" data-id="element-163" data-mp="gjw5c353qkzz">
        <motion.div {...fadeUp} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-164" data-mp="kk6ko6esanri">
          <h2 id="capability-governance" className="lg:col-span-5 font-heading font-bold text-3xl lg:text-4xl tracking-tight leading-tight" data-id="element-165" data-mp="uj4630gwy16a">
            Capability-Based Governance
          </h2>
          <div className="lg:col-span-7 space-y-6" data-id="element-166" data-mp="2w9pe8bdjs5ah">
            <p className="text-xl text-white leading-relaxed" data-id="element-167" data-mp="2wcldc314t00i">
              Cognocracy’s broader governance proposal explores linking
              eligibility for certain public responsibilities—and, in its more
              expansive form, some forms of civic influence—to assessed
              capability and periodic review.
            </p>
            <p className="text-lg text-white/80 leading-relaxed" data-id="element-168" data-mp="ol9x1trs0sz9">
              This raises substantive questions about equal political
              participation, assessment authority, appeals, and the limits of
              institutional power. These questions are part of the initiative’s
              research agenda.
            </p>
            <p className="font-heading font-semibold text-xl text-white leading-relaxed border-l-4 border-teal pl-6" data-id="element-169" data-mp="1c88b6ksfbou0">
              Political disagreement must be distinguished from poor reasoning.
              Assessment should evaluate the quality of reasoning rather than
              agreement with a preferred ideology.
            </p>
          </div>
        </motion.div>
      </section>

      <InitiativeSection id="continuous-development" title="Continuous Development" intro={['Participants could receive development roadmaps combining evidence-evaluation exercises, decision simulations, emotional regulation practice, and leadership preparation. Periodic reassessment would track change and identify further development needs.', 'Declining performance would be interpreted within its context and paired with a clear reassessment pathway. Mandatory participation or restrictions on access would require explicit rules and review.']} data-id="element-170" data-mp="3tj5tskwh6qzg" />

      <InitiativeSection id="open-questions" tone="muted" title="Research, Governance & Open Questions" data-id="element-171" data-mp="30rm8mvgbj2ut">
        <motion.ul {...fadeUp} className={`${offsetClass} grid grid-cols-1 md:grid-cols-2 gap-x-10 border-t border-line`} data-id="element-172" data-mp="35qfjeg480fra">
          {cognocracyOpenQuestions.map(question => <li key={question} className="flex gap-4 py-4 border-b border-line text-lg text-ink-dark leading-relaxed" data-id="element-173" data-mp="2wrakv9snsmf4">
              <span aria-hidden="true" className="mt-3 h-1.5 w-1.5 shrink-0 rounded-full bg-teal" data-id="element-174" data-mp="2d850cmmi8azv" />
              {question}
            </li>)}
        </motion.ul>
      </InitiativeSection>

      <section aria-labelledby="project-status" className="bg-white border-t border-line py-16 lg:py-20" data-id="element-175" data-mp="2m4nj5610uiiq">
        <motion.div {...fadeUp} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-16" data-id="element-176" data-mp="2w567oe3j6n6z">
          <h2 id="project-status" className="lg:col-span-5 font-heading font-bold text-3xl lg:text-4xl text-ink-dark tracking-tight" data-id="element-177" data-mp="2r5jsw0820yw2">
            Project Status
          </h2>
          <p className="lg:col-span-7 border-l-4 border-teal bg-teal-pale px-6 py-5 rounded-r-md text-lg text-ink-dark leading-relaxed" data-id="element-178" data-mp="30xzdz6ene3l6">
            Cognocracy is currently presented as a concept proposal. This page
            describes its intended design and research questions; it does not
            establish a validated assessment, deployed government system, or
            operational credential service.
          </p>
        </motion.div>
      </section>

      <section aria-labelledby="explore-foundation" className="bg-surface-bg border-t border-line py-20 lg:py-28" data-id="element-179" data-mp="1hy9kv7nk123g">
        <motion.div {...fadeUp} className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-180" data-mp="1lyaa7nc6yxpa">
          <h2 id="explore-foundation" className="font-heading font-bold text-3xl lg:text-5xl text-ink-dark tracking-tight leading-tight mb-6" data-id="element-181" data-mp="pa3yyhkrl721">
            Explore the Foundation
          </h2>
          <p className="text-xl text-ink-dark leading-relaxed mb-10" data-id="element-182" data-mp="ybka4rjeld7b">
            Learn how SyNeural connects knowledge, capability development, and
            an interactive workspace.
          </p>
          <ul className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-183" data-mp="29qqrhvo3ulnj">
            {cognocracyComponents.map((component, index) => <li key={component.href} data-id="element-184" data-mp="2orntjdp8471s" data-mpi={index}>
                <a href={component.href} className={`w-full inline-flex items-center justify-center gap-2 font-semibold px-6 py-3.5 rounded-md transition-colors duration-150 whitespace-nowrap focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal ${index === 0 ? 'bg-navy text-white hover:bg-navy-light' : 'border border-line bg-white text-navy hover:bg-surface-muted'}`} data-id="element-185" data-mp="2oqmgx4sk7chy">
                  {component.name === 'SKP' ? 'Semantic Knowledge Platform' : component.name}
                  <ArrowRight size={16} aria-hidden="true" data-id="element-186" data-mp="1w3ryedfayn74" />
                </a>
              </li>)}
          </ul>
        </motion.div>
      </section>
    </>;
}